import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = 'silk_admin_token';

export const adminAuth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (t) => localStorage.setItem(TOKEN_KEY, t),
  clearToken: () => localStorage.removeItem(TOKEN_KEY),
};

const authHeaders = () => {
  const token = adminAuth.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export function formatApiError(err, fallback = 'Something went wrong.') {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  }
  return String(detail);
}

export const adminApi = {
  login: async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    adminAuth.setToken(data.access_token);
    return data.user;
  },
  me: async () => {
    const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
    return data;
  },
  logout: () => {
    adminAuth.clearToken();
  },
  listInquiries: async () => {
    const { data } = await axios.get(`${API}/admin/inquiries`, { headers: authHeaders() });
    return data;
  },
  updateInquiry: async (id, payload) => {
    const { data } = await axios.patch(`${API}/admin/inquiries/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  convertInquiry: async (id) => {
    const { data } = await axios.post(`${API}/admin/inquiries/${id}/convert`, {}, { headers: authHeaders() });
    return data;
  },
  deleteInquiry: async (id) => {
    await axios.delete(`${API}/admin/inquiries/${id}`, { headers: authHeaders() });
  },
  listOrders: async () => {
    const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeaders() });
    return data;
  },
  createOrder: async (payload) => {
    const { data } = await axios.post(`${API}/admin/orders`, payload, { headers: authHeaders() });
    return data;
  },
  updateOrder: async (id, payload) => {
    const { data } = await axios.patch(`${API}/admin/orders/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  deleteOrder: async (id) => {
    await axios.delete(`${API}/admin/orders/${id}`, { headers: authHeaders() });
  },
  orderStats: async () => {
    const { data } = await axios.get(`${API}/admin/orders/stats`, { headers: authHeaders() });
    return data;
  },
  listOrdersFiltered: async ({ stage, since } = {}) => {
    const qs = new URLSearchParams();
    if (stage) qs.set('stage', stage);
    if (since) qs.set('since', since);
    const url = `${API}/admin/orders${qs.toString() ? `?${qs.toString()}` : ''}`;
    const { data } = await axios.get(url, { headers: authHeaders() });
    return data;
  },
  addOrderNote: async (id, text) => {
    const { data } = await axios.post(`${API}/admin/orders/${id}/notes`, { text }, { headers: authHeaders() });
    return data;
  },
  deleteOrderNote: async (id, index) => {
    const { data } = await axios.delete(`${API}/admin/orders/${id}/notes/${index}`, { headers: authHeaders() });
    return data;
  },
  markOrderSeen: async (id) => {
    const { data } = await axios.post(`${API}/admin/orders/${id}/seen`, {}, { headers: authHeaders() });
    return data;
  },
  markAllOrdersSeen: async () => {
    const { data } = await axios.post(`${API}/admin/orders/mark-all-seen`, {}, { headers: authHeaders() });
    return data;
  },
  // Products
  listProducts: async (type) => {
    const url = type ? `${API}/admin/products?type=${encodeURIComponent(type)}` : `${API}/admin/products`;
    const { data } = await axios.get(url, { headers: authHeaders() });
    return data;
  },
  // Content (CMS)
  getAllContent: async () => {
    const { data } = await axios.get(`${API}/content`);
    return data;
  },
  updateContent: async (key, payload) => {
    const { data } = await axios.put(`${API}/admin/content/${key}`, payload, { headers: authHeaders() });
    return data;
  },
  createProduct: async (payload) => {
    const { data } = await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
    return data;
  },
  updateProduct: async (id, payload) => {
    const { data } = await axios.patch(`${API}/admin/products/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  deleteProduct: async (id) => {
    await axios.delete(`${API}/admin/products/${id}`, { headers: authHeaders() });
  },
  // Catalog (Fabrics & Embroideries)
  listCatalog: async (type) => {
    const url = type ? `${API}/admin/catalog?type=${encodeURIComponent(type)}` : `${API}/admin/catalog`;
    const { data } = await axios.get(url, { headers: authHeaders() });
    return data;
  },
  createCatalog: async (payload) => {
    const { data } = await axios.post(`${API}/admin/catalog`, payload, { headers: authHeaders() });
    return data;
  },
  updateCatalog: async (id, payload) => {
    const { data } = await axios.patch(`${API}/admin/catalog/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  deleteCatalog: async (id) => {
    await axios.delete(`${API}/admin/catalog/${id}`, { headers: authHeaders() });
  },
  // ---------- Billing ----------
  listBillingCustomers: async () => {
    const { data } = await axios.get(`${API}/admin/billing/customers`, { headers: authHeaders() });
    return data;
  },
  createBillingCustomer: async (payload) => {
    const { data } = await axios.post(`${API}/admin/billing/customers`, payload, { headers: authHeaders() });
    return data;
  },
  updateBillingCustomer: async (id, payload) => {
    const { data } = await axios.put(`${API}/admin/billing/customers/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  deleteBillingCustomer: async (id) => {
    await axios.delete(`${API}/admin/billing/customers/${id}`, { headers: authHeaders() });
  },
  listInvoices: async () => {
    const { data } = await axios.get(`${API}/admin/billing/invoices`, { headers: authHeaders() });
    return data;
  },
  createInvoice: async (payload) => {
    const { data } = await axios.post(`${API}/admin/billing/invoices`, payload, { headers: authHeaders() });
    return data;
  },
  updateInvoice: async (id, payload) => {
    const { data } = await axios.put(`${API}/admin/billing/invoices/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  deleteInvoice: async (id) => {
    await axios.delete(`${API}/admin/billing/invoices/${id}`, { headers: authHeaders() });
  },
  recordPayment: async (id, payload) => {
    const { data } = await axios.post(`${API}/admin/billing/invoices/${id}/payments`, payload, { headers: authHeaders() });
    return data;
  },
  deletePayment: async (id, idx) => {
    const { data } = await axios.delete(`${API}/admin/billing/invoices/${id}/payments/${idx}`, { headers: authHeaders() });
    return data;
  },
  billingStats: async () => {
    const { data } = await axios.get(`${API}/admin/billing/stats`, { headers: authHeaders() });
    return data;
  },
  // ---------- Expenses ----------
  listExpenseOrders: async () => {
    const { data } = await axios.get(`${API}/admin/expenses/orders`, { headers: authHeaders() });
    return data;
  },
  createExpenseOrder: async (payload) => {
    const { data } = await axios.post(`${API}/admin/expenses/orders`, payload, { headers: authHeaders() });
    return data;
  },
  updateExpenseOrder: async (id, payload) => {
    const { data } = await axios.put(`${API}/admin/expenses/orders/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  deleteExpenseOrder: async (id) => {
    await axios.delete(`${API}/admin/expenses/orders/${id}`, { headers: authHeaders() });
  },
  listOverheads: async () => {
    const { data } = await axios.get(`${API}/admin/expenses/overheads`, { headers: authHeaders() });
    return data;
  },
  createOverhead: async (payload) => {
    const { data } = await axios.post(`${API}/admin/expenses/overheads`, payload, { headers: authHeaders() });
    return data;
  },
  updateOverhead: async (id, payload) => {
    const { data } = await axios.put(`${API}/admin/expenses/overheads/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  deleteOverhead: async (id) => {
    await axios.delete(`${API}/admin/expenses/overheads/${id}`, { headers: authHeaders() });
  },
  expenseStats: async () => {
    const { data } = await axios.get(`${API}/admin/expenses/stats`, { headers: authHeaders() });
    return data;
  },
  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${API}/admin/upload`, fd, {
      headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return data; // { url, filename, size }
  },
};

// Resolve relative /api/uploads/... paths to absolute URLs for <img>
export const resolveImageUrl = (image) => {
  if (!image) return '';
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  if (image.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${image}`;
  return image;
};

export const PRODUCT_TYPES = ['archive', 'ready-to-wear', 'suit'];
export const PRODUCT_STATUSES = ['Available', 'Limited', 'Made-to-Order', 'Sold'];
export const CATALOG_TYPES = ['fabric', 'embroidery'];

export const publicApi = {
  track: async (trackingId) => {
    const { data } = await axios.get(`${API}/track/${trackingId}`);
    return data;
  },
};

export const DELIVERY_STAGES = [
  'Inquiry Received',
  'In Atelier',
  'Embroidery in Progress',
  'Finishing & Quality Check',
  'Heritage Packaging',
  'Dispatched',
  'Delivered',
];
